// useNodeTypeFieldMutations.js

import { useCallback, useState } from 'react';

import { normalizeCalendarColor } from 'utilities/CalendarFieldColor';
import { normalizeCurrencyCode } from 'utilities/Currency';
import { generateKey } from 'utilities/Keys';
import { applyChecklistTemplateFieldProps } from 'utilities/ChecklistTemplateItems';
import { createSchemaField, insertSchemaFieldAtVisibleIndex } from 'utilities/NodeSchemaFields';
import { normalizeRelationDisplayFields } from 'utilities/RelationDisplayFields';
import { isRelationFieldType, isEnumCompatibleFieldType, isOptionsListFieldType } from 'utilities/FieldTypes';
import { isTemporalFieldType } from 'views/utils/temporalFieldFormat';
import {
  addNodeTypeField,
  deleteNodeTypeField,
  updateNodeTypeField,
} from 'services/nodetypes/NodeTypeMutationService';
import { syncFormsWithTypeSchema } from 'services/forms/FormSyncService';
import { FIELD_LOG_MESSAGES, FIELD_MESSAGES } from 'node/form/constants/fieldMessages';
import { ONCE_ONLY_FIELD_TYPES } from 'node/form/fieldmodal/constants';
import { findExistingOnceOnlyField } from 'node/form/fieldmodal/fieldTypeAvailability';

export default function useNodeTypeFieldMutations({
  isDisabled = false,
  fieldEditingField,
  fieldInsertIndex,
  fields,
  resolvedFields,
  overrideFields,
  setOverrideFields,
  resolvedTypeKey,
  resolvedBaseKey,
  getNodeKey,
  requireNodeKey = false,
  nodeNotReadyMessage = FIELD_MESSAGES.NODE_NOT_READY,
  typeNotReadyMessage = FIELD_MESSAGES.NODE_TYPE_NOT_READY,
  refreshNodeTypesCache,
  closeFieldModal,
  applyDefaultToNode,
}) {
  const [fieldSaving, setFieldSaving] = useState(false);

  const handleFieldSave = useCallback(async (fieldDraft) => {
    if (!fieldDraft?.label || !fieldDraft?.type || fieldSaving || isDisabled) return;

    const nodeKey = typeof getNodeKey === 'function' ? getNodeKey() : null;
    if (requireNodeKey && !nodeKey) {
      alert(nodeNotReadyMessage);
      return;
    }
    if (!resolvedTypeKey) {
      alert(typeNotReadyMessage);
      return;
    }

    const existingFieldWithType = findExistingOnceOnlyField({
      existingFields: resolvedFields,
      onceOnlyType: fieldDraft.type,
      typeKey: resolvedTypeKey,
    });
    if (ONCE_ONLY_FIELD_TYPES.includes(fieldDraft.type) && existingFieldWithType) {
      alert(`${fieldDraft.label || 'This field'} can only be added once for this type.`);
      return;
    }

    const nextField = createSchemaField({
      name: generateKey(),
      label: fieldDraft.label,
      fieldDescription: fieldDraft.fieldDescription,
      type: fieldDraft.type,
      targetType: fieldDraft.targetType,
      allowMultiple: fieldDraft.allowMultiple,
      showFilter: fieldDraft.showFilter,
      displayFields: fieldDraft.displayFields,
      showAccess: fieldDraft.showAccess,
      showAsProfileStack: fieldDraft.showAsProfileStack,
      checklistItems: fieldDraft.checklistItems,
      allowCustomItems: fieldDraft.allowCustomItems,
      defaultValue: fieldDraft.default,
      defaultUnit: fieldDraft.defaultUnit,
      currencyCode: fieldDraft.currencyCode,
      options: fieldDraft.options,
      enumOptionsSort: fieldDraft.enumOptionsSort,
      calendarColor: fieldDraft.calendarColor,
      displayInDefaultLists: fieldDraft.displayInDefaultLists === true,
      source: resolvedTypeKey,
    });

    const insertBeforeName =
      typeof fieldInsertIndex === 'number' && fieldInsertIndex < fields.length
        ? fields[fieldInsertIndex]?.name
        : null;

    setFieldSaving(true);
    try {
      await addNodeTypeField({
        nodeKey: nodeKey || undefined,
        typeKey: resolvedTypeKey,
        baseKey: resolvedBaseKey,
        field: nextField,
        insertBeforeName,
      });

      const baseFields = Array.isArray(overrideFields) ? overrideFields : resolvedFields;
      const nextFields = insertSchemaFieldAtVisibleIndex(baseFields, fields, fieldInsertIndex, nextField);
      setOverrideFields(nextFields);

      await refreshNodeTypesCache?.({
        baseKey: resolvedBaseKey,
        typeKey: resolvedTypeKey,
        fields: nextFields,
      });
      await syncFormsWithTypeSchema({
        typeDef: { schema: { fields: nextFields } },
        typeKey: resolvedTypeKey,
        baseKey: resolvedBaseKey,
      }).catch((error) => {
        console.error('[useNodeTypeFieldMutations] form sync failed after add', error);
      });
      applyDefaultToNode?.(nextField, fieldDraft.default);
      closeFieldModal?.();
    } catch (error) {
      console.error(FIELD_LOG_MESSAGES.ADD_FIELD_FAILED, error);
      alert(error?.message || FIELD_MESSAGES.ADD_FAILED);
    } finally {
      setFieldSaving(false);
    }
  }, [
    applyDefaultToNode,
    closeFieldModal,
    fields,
    getNodeKey,
    isDisabled,
    nodeNotReadyMessage,
    overrideFields,
    fieldInsertIndex,
    fieldSaving,
    refreshNodeTypesCache,
    requireNodeKey,
    resolvedFields,
    resolvedTypeKey,
    resolvedBaseKey,
    setOverrideFields,
    typeNotReadyMessage,
  ]);

  const handleFieldUpdate = useCallback(async (fieldDraft) => {
    if (!fieldDraft?.label || !fieldDraft?.type || fieldSaving || isDisabled) return;
    if (!fieldEditingField?.name) return;

    const nodeKey = typeof getNodeKey === 'function' ? getNodeKey() : null;
    if (requireNodeKey && !nodeKey) {
      alert(nodeNotReadyMessage);
      return;
    }
    if (!resolvedTypeKey) {
      alert(typeNotReadyMessage);
      return;
    }

    const nextField = {
      ...fieldEditingField,
      label: fieldDraft.label,
      fieldDescription: String(fieldDraft.fieldDescription || '').trim(),
      type: fieldEditingField.type,
      default: fieldDraft.default,
      options: isOptionsListFieldType(fieldEditingField) || fieldEditingField.type === 'measurement'
        ? fieldDraft.options
        : fieldEditingField.options,
      defaultUnit: fieldEditingField.type === 'measurement'
        ? fieldDraft.defaultUnit
        : fieldEditingField.defaultUnit,
      currencyCode: fieldEditingField.type === 'money'
        ? normalizeCurrencyCode(fieldDraft.currencyCode)
        : fieldEditingField.type === 'currency'
          ? undefined
          : fieldEditingField.currencyCode,
      enumOptionsSort: isEnumCompatibleFieldType(fieldEditingField)
        ? fieldDraft.enumOptionsSort
        : fieldEditingField.enumOptionsSort,
    };

    if (isRelationFieldType(fieldEditingField)) {
      nextField.targetType = fieldDraft.targetType || fieldEditingField.targetType || '';
      nextField.allowMultiple = fieldDraft.allowMultiple !== false;
      if (nextField.allowMultiple === false && fieldDraft.showFilter === true) {
        nextField.showFilter = true;
      } else {
        delete nextField.showFilter;
      }
      const nextDisplayFields = normalizeRelationDisplayFields(fieldDraft.displayFields);
      if (nextDisplayFields.length) {
        nextField.displayFields = nextDisplayFields;
      } else {
        delete nextField.displayFields;
      }
    }

    if (fieldEditingField.type === 'people') {
      if (fieldDraft.showAccess === true) {
        nextField.showAccess = true;
      } else {
        delete nextField.showAccess;
      }
      if (fieldDraft.showAsProfileStack === true) {
        nextField.showAsProfileStack = true;
      } else {
        delete nextField.showAsProfileStack;
      }
    }

    if (isTemporalFieldType(fieldEditingField.type)) {
      nextField.calendarColor = normalizeCalendarColor(fieldDraft.calendarColor);
    }

    if (fieldEditingField.type === 'checklist') {
      applyChecklistTemplateFieldProps(nextField, fieldDraft);
    } else {
      delete nextField.checklistItems;
      delete nextField.allowCustomItems;
      delete nextField.templateKeys;
    }

    if (fieldDraft.displayInDefaultLists === true) {
      nextField.displayInDefaultLists = true;
    } else {
      delete nextField.displayInDefaultLists;
    }

    setFieldSaving(true);
    try {
      await updateNodeTypeField({
        nodeKey: nodeKey || undefined,
        typeKey: resolvedTypeKey,
        baseKey: resolvedBaseKey,
        field: nextField,
      });

      const baseFields = Array.isArray(overrideFields) ? overrideFields : resolvedFields;
      const patch = {
        label: nextField.label,
        fieldDescription: nextField.fieldDescription,
        default: nextField.default,
      };
      if (isOptionsListFieldType(fieldEditingField) || fieldEditingField.type === 'measurement') {
        patch.options = nextField.options;
      }
      if (fieldEditingField.type === 'measurement') {
        patch.defaultUnit = nextField.defaultUnit;
      }
      if (fieldEditingField.type === 'money') {
        patch.currencyCode = nextField.currencyCode;
      }
      if (isEnumCompatibleFieldType(fieldEditingField)) {
        patch.enumOptionsSort = nextField.enumOptionsSort;
      }
      if (isRelationFieldType(fieldEditingField)) {
        patch.targetType = nextField.targetType;
        patch.allowMultiple = nextField.allowMultiple;
        if (nextField.showFilter === true) {
          patch.showFilter = true;
        } else {
          patch.showFilter = false;
        }
        patch.displayFields = Array.isArray(nextField.displayFields)
          ? nextField.displayFields
          : [];
      }
      if (fieldEditingField.type === 'people') {
        patch.showAccess = nextField.showAccess === true;
        patch.showAsProfileStack = nextField.showAsProfileStack === true;
      }
      if (isTemporalFieldType(fieldEditingField.type)) {
        patch.calendarColor = nextField.calendarColor;
      }
      if (nextField.displayInDefaultLists === true) {
        patch.displayInDefaultLists = true;
      } else {
        patch.displayInDefaultLists = false;
      }

      const nextFields = baseFields.map((field) => {
        if (field?.name !== fieldEditingField.name) {
          return field;
        }

        const nextPatchedField = { ...field, ...patch };
        if (fieldEditingField.type === 'checklist') {
          delete nextPatchedField.templateKeys;
        }
        if (isRelationFieldType(fieldEditingField) && nextPatchedField.showFilter !== true) {
          delete nextPatchedField.showFilter;
        }
        if (
          isRelationFieldType(fieldEditingField)
          && (!Array.isArray(nextPatchedField.displayFields) || nextPatchedField.displayFields.length === 0)
        ) {
          delete nextPatchedField.displayFields;
        }
        if (fieldEditingField.type === 'people') {
          if (nextPatchedField.showAccess !== true) {
            delete nextPatchedField.showAccess;
          }
          if (nextPatchedField.showAsProfileStack !== true) {
            delete nextPatchedField.showAsProfileStack;
          }
        }
        if (nextPatchedField.displayInDefaultLists !== true) {
          delete nextPatchedField.displayInDefaultLists;
        }

        return nextPatchedField;
      });
      setOverrideFields(nextFields);

      await refreshNodeTypesCache?.({
        baseKey: resolvedBaseKey,
        typeKey: resolvedTypeKey,
        fields: nextFields,
      });
      closeFieldModal?.();
    } catch (error) {
      console.error(FIELD_LOG_MESSAGES.UPDATE_FIELD_FAILED, error);
      alert(error?.message || FIELD_MESSAGES.UPDATE_FAILED);
    } finally {
      setFieldSaving(false);
    }
  }, [
    closeFieldModal,
    getNodeKey,
    isDisabled,
    nodeNotReadyMessage,
    overrideFields,
    fieldEditingField,
    fieldSaving,
    refreshNodeTypesCache,
    requireNodeKey,
    resolvedFields,
    resolvedTypeKey,
    resolvedBaseKey,
    setOverrideFields,
    typeNotReadyMessage,
  ]);

  const handleFieldDelete = useCallback(async () => {
    if (fieldSaving || isDisabled) return;
    if (!fieldEditingField?.name) return;
    const nodeKey = typeof getNodeKey === 'function' ? getNodeKey() : null;
    if (requireNodeKey && !nodeKey) {
      alert(nodeNotReadyMessage);
      return;
    }
    if (!resolvedTypeKey) {
      alert(typeNotReadyMessage);
      return;
    }

    setFieldSaving(true);
    try {
      await deleteNodeTypeField({
        nodeKey: nodeKey || undefined,
        typeKey: resolvedTypeKey,
        baseKey: resolvedBaseKey,
        fieldName: fieldEditingField.name,
      });

      const baseFields = Array.isArray(overrideFields) ? overrideFields : resolvedFields;
      const nextFields = baseFields.filter((field) => field?.name !== fieldEditingField.name);
      setOverrideFields(nextFields);

      await refreshNodeTypesCache?.({
        baseKey: resolvedBaseKey,
        typeKey: resolvedTypeKey,
        fields: nextFields,
      });
      await syncFormsWithTypeSchema({
        typeDef: { schema: { fields: nextFields } },
        typeKey: resolvedTypeKey,
        baseKey: resolvedBaseKey,
      }).catch((error) => {
        console.error('[useNodeTypeFieldMutations] form sync failed after delete', error);
      });
      closeFieldModal?.();
    } catch (error) {
      console.error(FIELD_LOG_MESSAGES.DELETE_FIELD_FAILED, error);
      alert(error?.message || FIELD_MESSAGES.DELETE_FAILED);
    } finally {
      setFieldSaving(false);
    }
  }, [
    closeFieldModal,
    getNodeKey,
    isDisabled,
    nodeNotReadyMessage,
    overrideFields,
    fieldEditingField,
    fieldSaving,
    refreshNodeTypesCache,
    requireNodeKey,
    resolvedFields,
    resolvedTypeKey,
    resolvedBaseKey,
    setOverrideFields,
    typeNotReadyMessage,
  ]);

  return {
    fieldSaving,
    handleFieldSave,
    handleFieldUpdate,
    handleFieldDelete,
  };
}